import { Flex, Skeleton, Stack, Text, Button, VStack, useToast } from '@chakra-ui/react';
import { Account } from '@/model';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { InputField } from '@/components/InputField';
import { formatCep } from '@/utils';
import { ModalFormData, modalFormSchema } from './schemas';
import { usePutAccountAddressMutation } from '@/services/queries/account';
import { PutAccountAddressRequest } from '@/services/request/account';
import { useTranslation } from 'react-i18next';

export interface LocalizationTabProps {
  isLoading?: boolean;
  item: Account;
}

export const LocalizationTab = ({ isLoading, item }: LocalizationTabProps) => {
  const putItemMutation = usePutAccountAddressMutation();
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<ModalFormData>({
    defaultValues: {
      postal_code: item?.address?.postal_code,
      line1: item?.address?.line1,
      line2: item?.address?.line2,
      neighborhood: item?.address?.neighborhood,
      line3: item?.address?.line3,
      city: item?.address?.city,
      state: item?.address?.state,
      country: item?.address?.country,
    },
    resolver: zodResolver(modalFormSchema),
  });
  const toast = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: ModalFormData) => {
    const payload: PutAccountAddressRequest = {
      postal_code: data?.postal_code,
      line1: data?.line1,
      line2: data?.line2,
      neighborhood: data?.neighborhood,
      line3: data?.line3,
      city: data?.city,
      state: data?.state,
      country: data?.country,
    };

    putItemMutation.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t('pages.accountdetail.components.tabs.localizationtab.success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.accountdetail.components.tabs.localizationtab.error'),
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!item) return;

    setValue('postal_code', formatCep(item?.address?.postal_code || ''));
    setValue('line1', item?.address?.line1);
    setValue('line2', item?.address?.line2);
    setValue('line3', item?.address?.line3);
    setValue('neighborhood', item?.address?.neighborhood || '');
    setValue('city', item?.address?.city || '');
    setValue('state', item?.address?.state || '');
    setValue('country', item?.address?.country || '');
  }, [item, setValue]);

  return (
    <Flex direction='column' gap='1.5rem'>
      {isLoading ? (
        <Skeleton w='160px' h='21px' />
      ) : (
        <Text fontWeight='bold'>{t('pages.accountdetail.components.tabs.localizationtab.title')}</Text>
      )}

      <Stack flex={1}>
        <VStack
          as='form'
          gap='kilo'
          align='start'
          onSubmit={handleSubmit(onSubmit)}
          maxWidth={{ base: '100%', md: '50%', lg: '30%' }}
        >
          {isLoading ? (
            <>
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
            </>
          ) : (
            <>
              <InputField
                withoutFloating
                label={t('pages.accountdetail.components.tabs.localizationtab.postal_code_label')}
                rootProps={{ isInvalid: !!errors?.postal_code, errorMessage: errors?.postal_code?.message }}
                {...register('postal_code')}
              />
              <InputField
                withoutFloating
                label={t('pages.accountdetail.components.tabs.localizationtab.street_label')}
                rootProps={{ isInvalid: !!errors?.line1, errorMessage: errors?.line1?.message }}
                {...register('line1')}
              />

              <Flex width='100%' gap='kilo' flexDirection={{ base: 'column', md: 'row' }}>
                <InputField
                  withoutFloating
                  label={t('pages.accountdetail.components.tabs.localizationtab.number_label')}
                  rootProps={{ isInvalid: !!errors?.line2, errorMessage: errors?.line2?.message }}
                  {...register('line2')}
                />

                <InputField
                  withoutFloating
                  label={t('pages.accountdetail.components.tabs.localizationtab.complement_label')}
                  rootProps={{ isInvalid: !!errors?.line3, errorMessage: errors?.line3?.message }}
                  {...register('line3')}
                />
              </Flex>

              <Flex width='100%' gap='kilo' flexDirection={{ base: 'column', md: 'row' }}>
                <InputField
                  withoutFloating
                  label={t('pages.accountdetail.components.tabs.localizationtab.neighborhood_label')}
                  rootProps={{ isInvalid: !!errors?.neighborhood, errorMessage: errors?.neighborhood?.message }}
                  {...register('neighborhood')}
                />

                <InputField
                  withoutFloating
                  label={t('pages.accountdetail.components.tabs.localizationtab.city_label')}
                  rootProps={{ isInvalid: !!errors?.city, errorMessage: errors?.city?.message }}
                  {...register('city')}
                />
              </Flex>

              <Flex width='100%' gap='kilo' flexDirection={{ base: 'column', md: 'row' }}>
                <InputField
                  withoutFloating
                  label={t('pages.accountdetail.components.tabs.localizationtab.state_label')}
                  rootProps={{ isInvalid: !!errors?.state, errorMessage: errors?.state?.message }}
                  {...register('state')}
                />

                <InputField
                  withoutFloating
                  label={t('pages.accountdetail.components.tabs.localizationtab.country_label')}
                  rootProps={{ isInvalid: !!errors?.country, errorMessage: errors?.country?.message }}
                  {...register('country')}
                />
              </Flex>
            </>
          )}

          {isLoading ? (
            <Skeleton width='138px' h='40px' />
          ) : (
            <Button type='submit'>{t('pages.accountdetail.components.tabs.localizationtab.button_submit')}</Button>
          )}
        </VStack>
      </Stack>

      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Flex>
  );
};
